import { useEffect, useRef, useState } from "react";

interface OrgOption {
  id: string;
  name: string;
  /** Logo cadastrada pela org (white-label); ausente = inicial do nome. */
  logoDataUrl?: string | null;
}

/** Avatar da org: logo quando houver, senão a inicial do nome. */
function OrgAvatar({ org, small = false }: { org?: OrgOption; small?: boolean }) {
  const cls = `org-switcher-avatar${small ? " small" : ""}`;
  if (org?.logoDataUrl) {
    return <img className={`${cls} org-switcher-logo`} src={org.logoDataUrl} alt="" aria-hidden />;
  }
  return (
    <span className={cls} aria-hidden>
      {org ? org.name.charAt(0).toUpperCase() : "★"}
    </span>
  );
}

interface Props {
  orgs: OrgOption[];
  value: string;
  onChange: (id: string) => void;
}

/**
 * Seletor de organização do sysadmin, no topo da nav da administração -
 * antes um <select> nativo solto; agora um dropdown customizado (mesmo
 * padrão do menu de conta na sidebar), com busca quando há muitas orgs.
 */
export function OrgSwitcher({ orgs, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const current = orgs.find((o) => o.id === value);
  const q = search.trim().toLowerCase();
  const filtered = q ? orgs.filter((o) => o.name.toLowerCase().includes(q)) : orgs;

  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <div className="org-switcher" ref={ref}>
      <span className="org-switcher-label">Configurando a organização</span>
      <button className="org-switcher-trigger" onClick={() => setOpen((v) => !v)} type="button" aria-expanded={open}>
        <OrgAvatar org={current} />
        <span className="org-switcher-name">{current ? current.name : "Minha própria org"}</span>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d={open ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} />
        </svg>
      </button>
      {open && (
        <div className="org-switcher-menu">
          {orgs.length > 6 && (
            <input
              className="org-switcher-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar organização…"
              autoFocus
            />
          )}
          <div className="org-switcher-list">
            <button
              className={`org-switcher-item ${!value ? "active" : ""}`}
              onClick={() => pick("")}
              type="button"
            >
              <span className="org-switcher-avatar small" aria-hidden>★</span>
              Minha própria org
            </button>
            {filtered.map((o) => (
              <button
                key={o.id}
                className={`org-switcher-item ${value === o.id ? "active" : ""}`}
                onClick={() => pick(o.id)}
                type="button"
              >
                <OrgAvatar org={o} small />
                {o.name}
              </button>
            ))}
            {filtered.length === 0 && <div className="org-switcher-empty">Nada encontrado.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
